import { useState, useEffect, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { DatePicker } from "@/components/ui/date-picker";
import { format } from "date-fns";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableCell,
  TableBody,
} from "@/components/ui/table";
import { MousePointerClick, Eye, FileSearch, ExternalLink } from "lucide-react";
import clsx from "clsx";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import {
  fetchGSCPerformance,
  fetchGSCQueries,
  fetchGSCTopPages,
  fetchGSCTrafficOverTime,
} from "@/services/gsc";
import {
  ResponsiveContainer,
  Label,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
} from "recharts";

import * as Tooltip from "@radix-ui/react-tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { useBrand } from "@/contexts/brand-context";
import { useIntegrations } from "@/contexts/integrations-context";
import { initiateGSCAuth } from "@/services/gsc";
import { toast } from "sonner";

const sortIcon = (active: boolean, dir: "asc" | "desc") => (
  <span className="inline-block ml-1 align-middle">
    <span
      className={clsx(
        "block text-xs leading-none",
        active && dir === "asc" ? "text-blue-600" : "text-gray-300",
      )}
    >
      ▲
    </span>
    <span
      className={clsx(
        "block -mt-1 text-xs leading-none",
        active && dir === "desc" ? "text-blue-600" : "text-gray-300",
      )}
    >
      ▼
    </span>
  </span>
);

export default function Analytics() {
  const { currentBrand } = useBrand();
  const { integrations, refreshIntegrations, getIntegrationsByType } =
    useIntegrations();
  const [gscConnected, setGscConnected] = useState<boolean | null>(null);
  const [gscProcessing, setGscProcessing] = useState(false);

  // Check GSC connection status when integrations change
  useEffect(() => {
    const gscIntegrations = getIntegrationsByType("search console");
    const isConnected = gscIntegrations.some(
      (integration) => integration.status === "active",
    );
    setGscConnected(isConnected);
  }, [integrations, getIntegrationsByType]);

  // Handler for Connect with Google (OAuth)
  const handleGSCConnect = async () => {
    if (!currentBrand) {
      return toast.error("Select a brand first");
    }
    setGscProcessing(true);
    let popup: Window | null = null;
    let pollTimer: number | null = null;
    try {
      popup = window.open("", "oauthGSC", "width=500,height=600");
      if (!popup) {
        setGscProcessing(false);
        alert("Popup was blocked. Please allow popups for this site.");
        return;
      }
      // Poll popup URL to detect redirects and closure
      pollTimer = window.setInterval(() => {
        if (!popup) return;
        if (popup.closed) {
          if (pollTimer) clearInterval(pollTimer);
        }
      }, 1000);
      await initiateGSCAuth(currentBrand.id, "webmaster");
      if (pollTimer) clearInterval(pollTimer);
      // After successful backend OAuth flow, refresh integrations and show success toast
      toast.success("Search Console connected");
      await refreshIntegrations();
    } catch (err: unknown) {
      if (err && typeof err === "object" && "message" in err) {
        toast.error(
          (err as { message?: string }).message || "Could not connect to GSC",
        );
      } else {
        toast.error("Could not connect to GSC");
      }
    } finally {
      setGscProcessing(false);
      if (popup && !popup.closed) popup.close();
      if (pollTimer) clearInterval(pollTimer);
    }
  };

  const [tab, setTab] = useState("queries");

  const [period, setPeriod] = useState<"7d" | "30d" | "90d" | "custom">("30d");
  // Custom date range state
  const [customRange, setCustomRange] = useState<{
    start: Date | undefined;
    end: Date | undefined;
  }>({ start: undefined, end: undefined });
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Type definitions
  // type Stat = {
  //   label: React.ReactNode;
  //   value: number;
  //   change: string;
  //   positive: boolean;
  //   icon: JSX.Element;
  //   sub?: string;
  // };

  // Memoize iconMap so it is stable for hooks
  // const iconMap = useMemo(
  //   () => ({
  //     click: <MousePointerClick className="w-7 h-7 text-blue-600" />,
  //     eye: <Eye className="w-7 h-7 text-green-600" />,
  //     "file-search": <FileSearch className="w-7 h-7 text-lime-600" />,
  //   }),
  //   [],
  // );

  // Compute start and end date strings for API or fallback
  const getDateRange = useCallback(() => {
    if (period === "custom" && customRange.start && customRange.end) {
      const startDate = customRange.start.toISOString().slice(0, 10);
      const endDate = customRange.end.toISOString().slice(0, 10);

      return {
        startDate,
        endDate,
      };
    }
    // Default ranges for each period
    const today = new Date();
    let startDate;
    const endDate = today.toISOString().slice(0, 10);
    if (period === "7d") {
      startDate = new Date(today.getTime() - 6 * 86400000)
        .toISOString()
        .slice(0, 10);
    } else if (period === "30d") {
      startDate = new Date(today.getTime() - 29 * 86400000)
        .toISOString()
        .slice(0, 10);
    } else if (period === "90d") {
      startDate = new Date(today.getTime() - 89 * 86400000)
        .toISOString()
        .slice(0, 10);
    } else {
      // fallback: last 7 days
      startDate = new Date(today.getTime() - 6 * 86400000)
        .toISOString()
        .slice(0, 10);
    }

    console.log(
      `[Analytics] Date range for ${period}: ${startDate} to ${endDate}`,
    );
    return { startDate, endDate };
  }, [period, customRange]);

  // Create stable query key for custom range
  const customRangeKey = useMemo(() => {
    if (period === "custom" && customRange.start && customRange.end) {
      return `${customRange.start.toISOString()}-${customRange.end.toISOString()}`;
    }
    return period;
  }, [period, customRange.start, customRange.end]);

  // React Query hooks for GSC data
  const { data: gscPerformance, isLoading: performanceLoading } = useQuery({
    queryKey: ["gsc-performance", currentBrand?.id, customRangeKey],
    queryFn: async () => {
      if (!currentBrand || !gscConnected) return null;
      const { startDate, endDate } = getDateRange();
      return await fetchGSCPerformance(currentBrand.id, startDate, endDate);
    },
    enabled:
      !!currentBrand &&
      gscConnected === true &&
      (period !== "custom" || (!!customRange.start && !!customRange.end)),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });

  const { data: queriesData = [] } = useQuery({
    queryKey: ["gsc-queries", currentBrand?.id, customRangeKey],
    queryFn: async () => {
      if (!currentBrand || !gscConnected) return [];
      const { startDate, endDate } = getDateRange();
      const queries = await fetchGSCQueries(
        currentBrand.id,
        startDate,
        endDate,
      );
      return queries || [];
    },
    enabled:
      !!currentBrand &&
      gscConnected === true &&
      (period !== "custom" || (!!customRange.start && !!customRange.end)),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const { data: topPages = [] } = useQuery({
    queryKey: ["gsc-top-pages", currentBrand?.id, customRangeKey],
    queryFn: async () => {
      if (!currentBrand || !gscConnected) return [];
      const { startDate, endDate } = getDateRange();
      return await fetchGSCTopPages(currentBrand.id, startDate, endDate);
    },
    enabled:
      !!currentBrand &&
      gscConnected === true &&
      (period !== "custom" || (!!customRange.start && !!customRange.end)),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const { data: trafficOverTime = [], isLoading: trafficLoading } = useQuery({
    queryKey: ["gsc-traffic-over-time", currentBrand?.id, customRangeKey],
    queryFn: async () => {
      if (!currentBrand || !gscConnected) return [];
      const { startDate, endDate } = getDateRange();
      return await fetchGSCTrafficOverTime(currentBrand.id, startDate, endDate);
    },
    enabled:
      !!currentBrand &&
      gscConnected === true &&
      (period !== "custom" || (!!customRange.start && !!customRange.end)),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  // Generate stats from performance data
  const stats = useMemo(() => {
    if (!gscPerformance) return [];

    return [
      {
        label: (
          <span className="flex items-center gap-1">
            Clicks
            <Tooltip.Root>
              <Tooltip.Trigger asChild>
                <span
                  tabIndex={0}
                  className="ml-1 cursor-pointer text-blue-500 hover:text-blue-700 focus:outline-none"
                  aria-label="Clicks info"
                  role="button"
                >
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="2"
                      fill="white"
                    />
                    <text
                      x="12"
                      y="16"
                      textAnchor="middle"
                      fontSize="13"
                      fill="currentColor"
                      fontFamily="Arial"
                      fontWeight="bold"
                    >
                      i
                    </text>
                  </svg>
                </span>
              </Tooltip.Trigger>
              <Tooltip.Content
                side="top"
                align="center"
                className="z-50 px-3 py-2 text-xs text-white bg-gray-900 rounded shadow-lg max-w-xs"
              >
                The number of times users clicked your website's link in Google
                Search results during the selected period.
                <Tooltip.Arrow className="fill-gray-900" />
              </Tooltip.Content>
            </Tooltip.Root>
          </span>
        ),
        value: gscPerformance.clicks,
        change:
          (gscPerformance.clicksGrowth >= 0 ? "+" : "") +
          gscPerformance.clicksGrowth.toFixed(1) +
          "%",
        positive: gscPerformance.clicksGrowth >= 0,
        icon: <MousePointerClick className="w-7 h-7 text-blue-600" />,
        sub: "vs previous period",
      },
      {
        label: (
          <span className="flex items-center gap-1">
            Impressions
            <Tooltip.Root>
              <Tooltip.Trigger asChild>
                <span
                  tabIndex={0}
                  className="ml-1 cursor-pointer text-blue-500 hover:text-blue-700 focus:outline-none"
                  aria-label="Impressions info"
                  role="button"
                >
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="2"
                      fill="white"
                    />
                    <text
                      x="12"
                      y="16"
                      textAnchor="middle"
                      fontSize="13"
                      fill="currentColor"
                      fontFamily="Arial"
                      fontWeight="bold"
                    >
                      i
                    </text>
                  </svg>
                </span>
              </Tooltip.Trigger>
              <Tooltip.Content
                side="top"
                align="center"
                className="z-50 px-3 py-2 text-xs text-white bg-gray-900 rounded shadow-lg max-w-xs"
              >
                The number of times your website's link appeared in Google
                Search results (regardless of clicks) during the selected
                period.
                <Tooltip.Arrow className="fill-gray-900" />
              </Tooltip.Content>
            </Tooltip.Root>
          </span>
        ),
        value: gscPerformance.impressions,
        change:
          (gscPerformance.impressionsGrowth >= 0 ? "+" : "") +
          gscPerformance.impressionsGrowth.toFixed(1) +
          "%",
        positive: gscPerformance.impressionsGrowth >= 0,
        icon: <Eye className="w-7 h-7 text-green-600" />,
        sub: "vs previous period",
      },
      {
        label: (
          <span className="flex items-center gap-1">
            Keywords
            <Tooltip.Root>
              <Tooltip.Trigger asChild>
                <span
                  tabIndex={0}
                  className="ml-1 cursor-pointer text-blue-500 hover:text-blue-700 focus:outline-none"
                  aria-label="Keyword count info"
                  role="button"
                >
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="2"
                      fill="white"
                    />
                    <text
                      x="12"
                      y="16"
                      textAnchor="middle"
                      fontSize="13"
                      fill="currentColor"
                      fontFamily="Arial"
                      fontWeight="bold"
                    >
                      i
                    </text>
                  </svg>
                </span>
              </Tooltip.Trigger>
              <Tooltip.Content
                side="top"
                align="center"
                className="z-50 px-3 py-2 text-xs text-white bg-gray-900 rounded shadow-lg max-w-xs"
              >
                The keyword count is the number of unique search queries
                (keywords) that brought impressions to your site during the
                selected period, as reported by Google Search Console.
                <Tooltip.Arrow className="fill-gray-900" />
              </Tooltip.Content>
            </Tooltip.Root>
          </span>
        ),
        value: gscPerformance.keywords,
        change: "+0.0%",
        positive: true,
        icon: <FileSearch className="w-7 h-7 text-lime-600" />,
        sub: "vs previous period",
      },
    ];
  }, [gscPerformance]);

  // Data for current period is used inline below; no need to declare unused variables.
  // Table sort state
  type QuerySortKey = "query" | "clicks" | "impressions" | "ctr" | "position";
  const [qSort, setQSort] = useState<{
    key: QuerySortKey;
    dir: "asc" | "desc";
  }>({ key: "clicks", dir: "desc" });

  // Sort logic
  const sortedQueries = [...queriesData].sort((a, b) => {
    const { key, dir } = qSort;
    if (key === "ctr" || key === "position") {
      return dir === "asc" ? a[key] - b[key] : b[key] - a[key];
    }
    if (typeof a[key] === "number" && typeof b[key] === "number") {
      return dir === "asc" ? a[key] - b[key] : b[key] - a[key];
    }
    return 0;
  });

  // Show CTA if GSC is not connected
  if (gscConnected === false) {
    return (
      <div className="flex justify-center items-center h-[60vh]">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader>
            <CardTitle>Connect Google Search Console</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4 text-sm text-muted-foreground">
              By connecting Google Search Console, you'll unlock powerful
              insights about your site's search performance directly inside your
              dashboard.
              <br />
              <span className="text-xs block mt-2">
                You'll be asked to grant read-only access to Search Console
                data.
              </span>
            </div>
            <button
              className="w-full flex items-center justify-center gap-2 border border-gray-300 rounded-lg py-2 px-4 bg-white hover:bg-gray-50 disabled:opacity-60"
              onClick={handleGSCConnect}
              disabled={gscProcessing}
            >
              <img src="/google.svg" alt="Google" className="w-5 h-5 mr-2" />
              {gscProcessing ? "Connecting..." : "Connect with Google"}
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto">
      {performanceLoading ? (
        <div>
          {/* Stats Skeleton */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-24 w-full rounded-lg" />
            ))}
          </div>
          {/* Table Skeleton */}
          <div className="mb-8">
            <Skeleton className="h-8 w-1/3 mb-4" />
            <Skeleton className="h-40 w-full rounded-lg" />
          </div>
        </div>
      ) : (
        <>
          <div className="mb-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-3xl font-bold tracking-tight">Analytics</h2>
                <p className="text-muted-foreground">
                  View your Google Search Console performance and search
                  analytics.
                </p>
              </div>
              <div className="flex items-center gap-4 mt-4 sm:mt-0">
                <div className="flex items-center gap-2 relative">
                  <Select
                    value={period}
                    onValueChange={(v) => {
                      setPeriod(v as typeof period);
                      if (v === "custom") {
                        setShowDatePicker(true);
                      } else {
                        setShowDatePicker(false);
                      }
                    }}
                  >
                    <SelectTrigger className="w-[120px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7d">7 days</SelectItem>
                      <SelectItem value="30d">30 days</SelectItem>
                      <SelectItem value="90d">90 days</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>

                  {period === "custom" && (
                    <button
                      className="border rounded-lg px-3 py-2 bg-white hover:bg-gray-50 text-sm flex items-center gap-2"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowDatePicker((v) => !v);
                      }}
                      type="button"
                    >
                      {customRange.start && customRange.end
                        ? `${format(customRange.start, "MMM d, yyyy")} - ${format(customRange.end, "MMM d, yyyy")}`
                        : "Select date range"}
                    </button>
                  )}

                  {period === "custom" && showDatePicker && (
                    <div className="absolute top-full right-0 mt-1 p-3 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[300px]">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">
                          Select date range
                        </span>
                        <button
                          className="p-1 hover:bg-gray-100 rounded"
                          onClick={() => setShowDatePicker(false)}
                        >
                          ✕
                        </button>
                      </div>

                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Start Date
                          </label>
                          <DatePicker
                            selectedDate={customRange.start}
                            onDateChange={(date) =>
                              setCustomRange((prev) => ({
                                ...prev,
                                start: date,
                              }))
                            }
                            placeholder="Select start date"
                            className="w-full"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            End Date
                          </label>
                          <DatePicker
                            selectedDate={customRange.end}
                            onDateChange={(date) =>
                              setCustomRange((prev) => ({ ...prev, end: date }))
                            }
                            placeholder="Select end date"
                            className="w-full"
                            minDate={customRange.start || new Date()}
                          />
                        </div>
                      </div>

                      {customRange.start && customRange.end && (
                        <div className="mt-3 p-2 bg-gray-50 rounded text-xs text-gray-600">
                          <div className="flex justify-between">
                            <span>
                              From: {format(customRange.start, "MMM d, yyyy")}
                            </span>
                            <span>
                              To: {format(customRange.end, "MMM d, yyyy")}
                            </span>
                          </div>
                        </div>
                      )}

                      <div className="mt-3 flex justify-end">
                        <button
                          className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                          onClick={() => setShowDatePicker(false)}
                          disabled={!customRange.start || !customRange.end}
                        >
                          Done
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {stats.map((stat, idx) => (
                <Card
                  key={
                    typeof stat.label === "string" ? stat.label : `stat-${idx}`
                  }
                  className="shadow-sm rounded-2xl"
                >
                  <CardHeader className="flex flex-row items-center gap-4 pb-2">
                    <div className="bg-gray-100 rounded-full p-3 flex items-center justify-center">
                      {stat.icon}
                    </div>
                    <div>
                      <CardTitle className="text-base font-medium text-gray-700">
                        {stat.label}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="text-3xl font-bold text-gray-900">
                      {stat.value.toLocaleString()}
                    </div>
                    <div
                      className={clsx(
                        "text-xs font-medium mt-1",
                        stat.positive ? "text-green-600" : "text-red-600",
                      )}
                    >
                      {stat.change}{" "}
                      <span className="text-gray-400">{stat.sub}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Traffic Over Time Chart Card (Recharts version) */}
            <Card className="mt-8">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">
                  Traffic Over Time
                </CardTitle>
                <div className="text-sm text-muted-foreground">
                  Daily clicks and impressions for the selected period.
                </div>
              </CardHeader>
              <CardContent>
                {trafficLoading ? (
                  <Skeleton className="w-full h-64 rounded" />
                ) : (
                  <div style={{ height: 320 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={trafficOverTime}
                        margin={{ top: 16, right: 32, left: 0, bottom: 8 }}
                      >
                        <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 12 }}
                          tickFormatter={(date) => {
                            try {
                              return format(new Date(date), "MMM d");
                            } catch {
                              return date;
                            }
                          }}
                        />
                        {/* Left Y-axis for Impressions */}
                        <YAxis
                          yAxisId="left"
                          tick={{ fontSize: 12 }}
                          tickFormatter={(v) =>
                            v >= 1000 ? `${v / 1000}K` : v
                          }
                          domain={[0, "auto"]}
                        >
                          <Label
                            value="Impressions"
                            position="top"
                            offset={-25}
                            dx={10}
                            style={{
                              fontSize: 13,
                              fill: "#06b6d4",
                              fontWeight: 500,
                            }}
                          />
                        </YAxis>
                        {/* Right Y-axis for Clicks */}
                        <YAxis
                          yAxisId="right"
                          orientation="right"
                          tick={{ fontSize: 12 }}
                          tickFormatter={(v) =>
                            v >= 1000 ? `${v / 1000}K` : v
                          }
                          domain={[0, "auto"]}
                        >
                          <Label
                            value="Clicks"
                            position="top"
                            offset={-10}
                            dx={30}
                            style={{
                              fontSize: 13,
                              fill: "#2563eb",
                              fontWeight: 500,
                              textAnchor: "end",
                            }}
                          />
                        </YAxis>
                        <RechartsTooltip />

                        {/* Clicks Line (right axis) */}
                        <Line
                          type="monotone"
                          dataKey="clicks"
                          name="Clicks"
                          stroke="#2563eb"
                          strokeWidth={2}
                          dot={false}
                          isAnimationActive={false}
                          yAxisId="right"
                        />
                        {/* Impressions Line (left axis, dashed, filled) */}
                        <Line
                          type="monotone"
                          dataKey="impressions"
                          name="Impressions"
                          stroke="#06b6d4"
                          strokeWidth={2}
                          dot={false}
                          strokeDasharray="5 5"
                          isAnimationActive={false}
                          fillOpacity={0.15}
                          fill="url(#impressionsGradient)"
                          yAxisId="left"
                        />
                        {/* Gradient fill for Impressions */}
                        <defs>
                          <linearGradient
                            id="impressionsGradient"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="5%"
                              stopColor="#06b6d4"
                              stopOpacity={0.25}
                            />
                            <stop
                              offset="95%"
                              stopColor="#06b6d4"
                              stopOpacity={0.05}
                            />
                          </linearGradient>
                        </defs>
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="mb-6"></div>
          </div>
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="queries">Top Keywords</TabsTrigger>
              <TabsTrigger value="top-pages">Top Pages</TabsTrigger>
            </TabsList>
            <TabsContent value="queries">
              <Card className="bg-white rounded-2xl shadow-sm">
                <CardContent className="p-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead
                          className="min-w-[140px] cursor-pointer"
                          onClick={() =>
                            setQSort((s) => ({
                              key: "query",
                              dir:
                                s.key === "query" && s.dir === "asc"
                                  ? "desc"
                                  : "asc",
                            }))
                          }
                        >
                          Query {sortIcon(qSort.key === "query", qSort.dir)}
                        </TableHead>
                        <TableHead
                          className="cursor-pointer"
                          onClick={() =>
                            setQSort((s) => ({
                              key: "clicks",
                              dir:
                                s.key === "clicks" && s.dir === "asc"
                                  ? "desc"
                                  : "asc",
                            }))
                          }
                        >
                          Clicks {sortIcon(qSort.key === "clicks", qSort.dir)}
                        </TableHead>
                        <TableHead
                          className="cursor-pointer"
                          onClick={() =>
                            setQSort((s) => ({
                              key: "impressions",
                              dir:
                                s.key === "impressions" && s.dir === "asc"
                                  ? "desc"
                                  : "asc",
                            }))
                          }
                        >
                          Impressions{" "}
                          {sortIcon(qSort.key === "impressions", qSort.dir)}
                        </TableHead>
                        <TableHead
                          className="cursor-pointer"
                          onClick={() =>
                            setQSort((s) => ({
                              key: "ctr",
                              dir:
                                s.key === "ctr" && s.dir === "asc"
                                  ? "desc"
                                  : "asc",
                            }))
                          }
                        >
                          CTR {sortIcon(qSort.key === "ctr", qSort.dir)}
                        </TableHead>
                        <TableHead
                          className="cursor-pointer"
                          onClick={() =>
                            setQSort((s) => ({
                              key: "position",
                              dir:
                                s.key === "position" && s.dir === "asc"
                                  ? "desc"
                                  : "asc",
                            }))
                          }
                        >
                          Position{" "}
                          {sortIcon(qSort.key === "position", qSort.dir)}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedQueries.map((row) => (
                        <TableRow key={row.query} className="hover:bg-gray-50">
                          <TableCell className="font-medium text-gray-900">
                            {row.query}
                          </TableCell>
                          <TableCell>{row.clicks.toLocaleString()}</TableCell>
                          <TableCell>
                            {row.impressions.toLocaleString()}
                          </TableCell>
                          <TableCell>{row.ctr.toFixed(2)}%</TableCell>
                          <TableCell>{row.position.toFixed(1)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="top-pages">
              <Card className="bg-white rounded-2xl shadow-sm">
                <CardContent className="p-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[200px]">
                          Page URL
                        </TableHead>
                        <TableHead>Clicks</TableHead>
                        <TableHead>Impressions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Array.isArray(topPages) && topPages.length > 0 ? (
                        topPages.map((row) => (
                          <TableRow
                            key={row.pageUrl}
                            className="hover:bg-gray-50"
                          >
                            <TableCell>
                              <a
                                href={row.pageUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-700 hover:underline flex items-center gap-1"
                              >
                                {row.pageUrl}{" "}
                                <ExternalLink className="w-4 h-4 inline-block text-gray-400" />
                              </a>
                            </TableCell>
                            <TableCell>{row.clicks.toLocaleString()}</TableCell>
                            <TableCell>
                              {row.impressions.toLocaleString()}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={3}
                            className="text-gray-400 italic"
                          >
                            No data
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
